import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Scissors } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useProfile } from "@/context/ProfileContext";
import api from "@/lib/api";
import Avatar from "@/components/Avatar";

export default function Hair() {
  const { lang, L } = useI18n();
  const { profile, options, hasProfile } = useProfile();
  const [data, setData] = useState(null);
  const [previewHair, setPreviewHair] = useState(null);

  useEffect(() => {
    api.post("/hair", profile).then(({ data }) => setData(data));
  }, [profile]);

  return (
    <div className="bg-surface min-h-screen">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <p className="text-xs uppercase tracking-[0.3em] text-rose-500 font-semibold">Sistema Capelli</p>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl">Il taglio e il colore ideali per te</h1>
        <p className="mt-3 text-charcoal/55 max-w-xl">Consigli basati sulla forma del viso, sulla tua armocromia e sul colore di occhi e pelle.</p>
        {!hasProfile && <p className="mt-3 text-sm text-rose-500"><Link to="/quiz" className="underline font-semibold">Fai il quiz</Link> per consigli personalizzati.</p>}

        <div className="grid lg:grid-cols-[320px_1fr] gap-8 mt-8 items-start">
          <div className="bg-white rounded-2xl border border-rose-100 p-5 text-center lg:sticky lg:top-24">
            <p className="text-xs uppercase tracking-widest text-charcoal/40 mb-3">Anteprima colore</p>
            <Avatar profile={profile} options={options} size={260} lip="#C97F8E" hairHex={previewHair} />
            <p className="mt-2 text-sm text-charcoal/50">{previewHair ? "Colore in prova" : "Il tuo colore attuale"}</p>
          </div>

          <div>
            {/* colors */}
            {data?.colors?.length > 0 && (
              <div className="mb-10">
                <h2 className="font-serif text-2xl mb-4">Colori consigliati</h2>
                <div className="flex flex-wrap gap-4">
                  {data.colors.map((c, i) => (
                    <button key={i} onMouseEnter={() => setPreviewHair(c.hex)} onMouseLeave={() => setPreviewHair(null)} data-testid={`hair-color-${i}`}
                      className="text-center group">
                      <span className="block w-16 h-16 rounded-full ring-2 ring-white shadow-md group-hover:scale-110 transition-transform" style={{ background: c.hex }} />
                      <span className="mt-2 block text-xs font-medium text-charcoal/70">{L(c, "name")}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* hairstyles */}
            <h2 className="font-serif text-2xl mb-4">Tagli e acconciature consigliati</h2>
            <div className="grid sm:grid-cols-2 gap-4" data-testid="hairstyles-grid">
              {(data?.hairstyles || []).map((hs, i) => (
                <motion.div key={hs.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-2xl border border-rose-100 p-5 flex gap-4 hover-lift" data-testid={`hairstyle-${hs.id}`}>
                  <span className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center shrink-0"><Scissors size={20} /></span>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-serif text-lg">{L(hs, "name")}</h3>
                      <span className="text-xs font-bold text-white rounded-full px-2 py-0.5" style={{ background: hs.match >= 85 ? "#C9A45E" : "#DDA7A5" }}>{hs.match}%</span>
                    </div>
                    <p className="mt-1 text-sm text-charcoal/55">{L(hs, "desc")}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
